import { Box } from '@mui/material'
import { RendererProps, getStringPath, noop, resolveDynamicString } from 'components/a2ui/types'
import { RenderField } from 'components/core/dynamic-form/fields/RenderField'
import { executeValidationChecks, FunctionCallCheck, getByPointer } from 'lib/a2ui/resolver'
import { Field } from 'pbts/c1/api/form/v1/form'
import { ChangeEvent, FC, useCallback, useMemo } from 'react'

type TextFieldRendererProps = Pick<RendererProps, 'component' | 'dataModel' | 'onDataChange' | 'disabled'>

/**
 * Renders a text input field using C1's RenderField component.
 * Maps the A2UI TextField component to a Field proto structure.
 * Executes client-side validation checks and displays errors.
 */
export const TextFieldRenderer: FC<TextFieldRendererProps> = ({
    component,
    dataModel,
    onDataChange,
    disabled = false,
}) => {
    const tf = component.component.oneofKind === 'textField' ? component.component.textField : undefined
    const label = useMemo(
        () => (tf ? resolveDynamicString(tf.label, dataModel, getByPointer) : ''),
        [tf, dataModel],
    )
    const placeholder = useMemo(
        () => (tf ? resolveDynamicString(tf.placeholder, dataModel, getByPointer) : ''),
        [tf, dataModel],
    )
    const path = tf ? getStringPath(tf.value) : undefined
    const value = path ? String(getByPointer(dataModel, path) ?? '') : ''

    // Execute validation checks and collect errors
    const validationErrors = useMemo(() => {
        if (!tf?.checks?.length) return []
        // Build checks with resolved value path
        const checksWithValue: FunctionCallCheck[] = tf.checks.map((check) => ({
            ...check,
            args: { ...check.args, value: path || '' },
        }))
        return executeValidationChecks(checksWithValue, dataModel)
    }, [tf?.checks, dataModel, path])

    const hasError = validationErrors.length > 0
    const errorMessage = validationErrors.map((e) => e.message).join(', ')

    const field: Field = useMemo(() => ({
        name: component.id,
        displayName: label,
        description: '',
        required: false,
        type: {
            oneofKind: 'stringField',
            stringField: {
                defaultValue: '',
                placeholder,
                view: {
                    oneofKind: 'textField',
                    textField: { multiline: tf?.multiline ?? false },
                },
            },
        },
        providerConfig: { oneofKind: undefined },
    }), [component.id, label, placeholder, tf?.multiline])

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
        if (path) onDataChange(path, e.target.value)
    }, [path, onDataChange])

    const cField = useMemo(() => ({
        value,
        onChange: handleChange,
        onBlur: noop,
        name: component.id,
        ref: noop,
    }), [value, handleChange, component.id])

    if (!tf) return null

    // Convert validation errors to react-hook-form format for RenderField
    const errors = hasError ? { [component.id]: { type: 'validation', message: errorMessage } } : {}

    return (
        <Box>
            <RenderField field={field} cField={cField} errors={errors} disabled={disabled} />
        </Box>
    )
}
